import { Box, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import Paper from '@mui/material/Paper';
import LayoutTheme from './HoverTheme';
import { theme } from '@/theme/theme';
import { Page } from '@sm-dashboard/types';
import { incomesDashboardRoute, incomesRoute, standingOrdersRoute } from '@/constants';
import { useAppContext } from '@/context/context';
import { isWorldShalichUser } from '@/constants';
import { usePaymentConfig } from '@/hooks/usePaymentConfig';

const financeLinks = new Set([incomesDashboardRoute, incomesRoute, standingOrdersRoute]);

interface HoverMenuProps {
  openMenuIndex: number;
  menuChildren: any[];
  handleMouseLeave: (index: number) => void;
  handleMenuClose: (index: number, option?: Page) => void;
  handleMouseEnterMenu: (index: number) => void;
  drawerWidth: number;
}

export const HoverMenu = ({
  openMenuIndex,
  menuChildren,
  handleMouseLeave,
  handleMenuClose,
  handleMouseEnterMenu,
  drawerWidth
}: HoverMenuProps) => {
  const { userPermission, shaliach, user } = useAppContext();
  const hideChabadIsraelLogo = isWorldShalichUser(userPermission);
  const shaliachId = shaliach?.id ?? user?.id;
  const { hasPaymentConfig, configuredProviderType, loading: paymentConfigLoading } = usePaymentConfig(shaliachId);
  const showFinance = Boolean(hasPaymentConfig && configuredProviderType === 'cardcom');
  const hideFinance = Boolean(shaliachId) && !paymentConfigLoading && !showFinance;
  const visibleChildren =
    menuChildren?.filter(
      (option: Page) => !hideFinance || (!option.isFinanceHeader && !financeLinks.has(option.link || ''))
    ) || [];
  const canOpenLink = (link?: string) =>
    Boolean(userPermission?.isAdmin || userPermission?.isSuperAdmin || userPermission?.hasPagePermission(link || ''));
  const hasSectionHeaders = visibleChildren.some(
    (option: Page) =>
      option.isHeader ||
      option.isKedmaHeader ||
      option.isMessengersHeader ||
      option.isPersonalHeader ||
      option.isFinanceHeader
  );

  return (
    <Paper
      onMouseEnter={() => handleMouseEnterMenu(openMenuIndex)}
      onMouseLeave={() => handleMouseLeave(openMenuIndex)}
      sx={{
        position: 'absolute',
        top: 0,
        left: `calc(${drawerWidth}px + 16px)`,
        minWidth: drawerWidth * 2,
        height: 'calc(100% - 32px)',
        mt: 2,
        boxSizing: 'border-box',
        borderRadius: 2,
        boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.25)',
        py: 1,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'primary.main',
        animation: 'slideIn 0.3s ease-out',
        '@keyframes slideIn': {
          '0%': {
            opacity: 0,
            transform: 'translateX(-20px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)'
          }
        }
      }}
    >
      <List
        sx={{
          pt: hasSectionHeaders ? 15 : 20,
          px: 1,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        {visibleChildren.map((option: any, optIndex: number) =>
          option.isHeader ? (
            hideChabadIsraelLogo ? null : (
              <LayoutTheme type="logo" key={optIndex} />
            )
          ) : option.isKedmaHeader ? (
            <LayoutTheme type="kedma" key={optIndex} />
          ) : option.isMessengersHeader ? (
            <LayoutTheme type="messengers" key={optIndex} />
          ) : option.isPersonalHeader ? (
            <LayoutTheme type="personal" key={optIndex} />
          ) : option.isFinanceHeader ? (
            <LayoutTheme type="finance" key={optIndex} />
          ) : !(option.menuAction || canOpenLink(option?.link)) ? null : (
            <Box key={optIndex} sx={{ mb: 0.5 }}>
              <ListItem
                onClick={() => handleMenuClose(openMenuIndex, option)}
                sx={{
                  fontSize: 13,
                  pl: 1,
                  color: 'common.white',
                  cursor: 'pointer',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#FFFFFF1A',
                  borderRadius: '7px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)'
                  }
                }}
              >
                {option?.icon && (
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      border: '1px solid #FFFFFF33',
                      backgroundColor: '#FFFFFF4D',
                      borderRadius: '4px',
                      color: theme.palette.primary.main,
                      '& .MuiSvgIcon-root, & .SmIcon-root': {
                        color: theme.palette.primary.main,
                        fontSize: '1.2rem',
                        width: '1.2rem',
                        height: '1.2rem'
                      }
                    }}
                  >
                    {option?.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={option?.primary}
                  primaryTypographyProps={{
                    variant: 'caption'
                  }}
                  sx={{
                    flex: 'unset',
                    width: 'auto',
                    m: 0,
                    p: 0
                  }}
                />
              </ListItem>
            </Box>
          )
        )}
      </List>
    </Paper>
  );
};

export default HoverMenu;
